import React, { Fragment, useState } from 'react'
import Clear from '@material-ui/icons/Clear'
import { CircularProgress, FormControl, Grid } from '@material-ui/core'
import { StyledButton, StyledDialogContent, StyledDialogTitle } from '../../../../../../theme/Global'
import { FilledInputStyle, LabelStyle } from '../../../../../../theme/Forms'
import { labelStyle, warningNoteContainerStyle, warningNoteStyle } from './styles'
import { maxLength } from '../../../../../../utilities/stringLengthUtility'
import { localeNames } from '../../../../../../constants/localeNames'

const EditLocationAddressModal = ({
  handleClose,
  unsetHeight,
  location,
  loading,
  handleSave
}) => {
  const locationAddress = location.addresses[0]
  const title = `Edit ${location.displayName}`

  const [attention, setAttention] = useState(locationAddress.attention)
  const [address1, setAddress1] = useState(locationAddress.address1)
  const [address2, setAddress2] = useState(locationAddress.address2)
  const [city, setCity] = useState(locationAddress.city)
  const [zip, setZip] = useState(locationAddress.zip)

  const addressDto = {
    attention,
    address1,
    address2,
    address3: locationAddress.address3,
    city,
    county: locationAddress.county,
    country: locationAddress.country,
    state: locationAddress.state,
    type: locationAddress.type,
    zip
  }

  const cleanUpAddressField = (field) => field.replace(/^([`"'“”‘’�\s]|(ï¿½))+|([`"'“”‘’�\s]|(ï¿½))+$/g, '')

  const isMissingRequiredFields = () => {
    return !(address1.trim()
      && city.trim()
      && zip.trim())
  }

  const maxInputLengths = {
    attention: 35,
    address1: 35,
    address2: 35,
    city: 35,
    zip: 10
  }

  const hasInvalidInputs = () => {
    return [
      maxLength(attention, maxInputLengths.attention),
      maxLength(address1, maxInputLengths.address1),
      maxLength(address2, maxInputLengths.address2),
      maxLength(city, maxInputLengths.city),
      maxLength(zip, maxInputLengths.zip)
    ].includes(false)
  }

  const isValidUSPostalCode = () => {
    const cleanedZip = cleanUpAddressField(zip)
    const isNumeric = /^(\d+-?)?\d+$/.test(cleanedZip)
    const isCorrectFormat = /^\d{5}(?:-\d{4})?$/.test(cleanedZip)
    return isNumeric && isCorrectFormat
  }

  const isValidCanadaPostalCode = () => {
    const cleanedZip = cleanUpAddressField(zip)
    const isAlphanumeric = /^[A-Za-z0-9]+ [A-Za-z0-9]+$/.test(cleanedZip)
    const isCorrectFormat = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(cleanedZip)
    return isAlphanumeric && isCorrectFormat
  }

  const isInvalidPostalCode = () => {
    const cleanedUpCountry = cleanUpAddressField(locationAddress.country)
    if (cleanedUpCountry === 'US') {
      return !isValidUSPostalCode()
    } if (cleanedUpCountry === 'CA') {
      return !isValidCanadaPostalCode()
    }
    return false
  }

  return (
    <Fragment>
      {title
        ? (
          <StyledDialogTitle>
            {title}
            <Clear
              onClick={handleClose}
              style={{
                float: 'right',
                height: '45px',
                width: '45px'
              }}
            />
          </StyledDialogTitle>
        )
        : null}
      <StyledDialogContent style={unsetHeight ? { minHeight: 'unset' } : {}}>
        <Grid style={{ marginTop: '1rem' }} container spacing={8}>
          <Grid container spacing={24} alignContent="flex-start" justify="flex-start">
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="attention">
                  Attn
                  {maxInputLengths.attention - attention.length <= 5 && (
                    <span style={{ float: 'right' }}>
                      {attention.length}/{maxInputLengths.attention} characters
                    </span>
                  )}
                </LabelStyle>
                <FilledInputStyle
                  name="attention"
                  type="text"
                  variant="filled"
                  disabled={loading}
                  error={!maxLength(attention, maxInputLengths.attention)}
                  value={attention || ''}
                  onChange={(event) => setAttention(event.target.value.slice(0, maxInputLengths.attention))}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={24} alignContent="flex-start" justify="flex-start">
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="address1">
                  Address 1
                  {maxInputLengths.address1 - address1.length <= 5 && (
                    <span style={{ float: 'right' }}>
                      {address1.length}/{maxInputLengths.address1} characters
                    </span>
                  )}
                </LabelStyle>
                <FilledInputStyle
                  name="address1"
                  type="text"
                  variant="filled"
                  disabled={loading}
                  error={!maxLength(address1, maxInputLengths.address1) || address1 === ''}
                  value={address1 || ''}
                  onChange={(event) => setAddress1(event.target.value.slice(0, maxInputLengths.address1))}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="address2">
                  Address 2
                  {maxInputLengths.address2 - address2.length <= 5 && (
                    <span style={{ float: 'right' }}>
                      {address2.length}/{maxInputLengths.address2} characters
                    </span>
                  )}
                </LabelStyle>
                <FilledInputStyle
                  name="address2"
                  type="text"
                  variant="filled"
                  disabled={loading}
                  error={!maxLength(address2, maxInputLengths.address2)}
                  value={address2 || ''}
                  onChange={(event) => setAddress2(event.target.value.slice(0, maxInputLengths.address2))}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={24} alignContent="flex-start" justify="flex-start">
            <Grid item xs={4} sm={4}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="city">
                  City
                  {maxInputLengths.city - city.length <= 5 && (
                    <span style={{ float: 'right' }}>
                      {city.length}/{maxInputLengths.city} characters
                    </span>
                  )}
                </LabelStyle>
                <FilledInputStyle
                  name="city"
                  type="text"
                  variant="filled"
                  error={!maxLength(city, maxInputLengths.city) || city === ''}
                  disabled={loading}
                  value={city || ''}
                  onChange={(event) => setCity(event.target.value.slice(0, maxInputLengths.city))}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={4}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="state">State/Province/Territory</LabelStyle>
                <FilledInputStyle
                  name="state"
                  type="text"
                  variant="filled"
                  disabled
                  value={localeNames
                    .filter((locale) => locale.type === 2)
                    .find((state) => state.localeID === locationAddress.state).name}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={4}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="zip">
                  Postal Code
                  {maxInputLengths.zip - zip.length <= 3 && (
                    <span style={{ float: 'right' }}>
                      {zip.length}/{maxInputLengths.zip} characters
                    </span>
                  )}
                </LabelStyle>
                <FilledInputStyle
                  name="zip"
                  type="text"
                  variant="filled"
                  error={isInvalidPostalCode()}
                  disabled={loading}
                  placeholder={locationAddress.country === 'US' ? '12345 or 12345-6789' : 'A1A 1A1'}
                  value={zip || ''}
                  onChange={(event) => setZip(event.target.value.slice(0, maxInputLengths.zip))}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={24} alignContent="flex-start" justify="flex-start">
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth>
                <LabelStyle style={labelStyle} htmlFor="country">Country</LabelStyle>
                <FilledInputStyle
                  name="country"
                  type="text"
                  variant="filled"
                  disabled
                  value={localeNames
                    .filter((locale) => locale.type === 1)
                    .find((country) => country.localeID === locationAddress.country).name}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} style={warningNoteContainerStyle}>
            <div style={warningNoteStyle} htmlFor="warning-note">
              Please note: It may take several minutes for changes to be reflected in your locations list.
            </div>
          </Grid>
          <Grid
            container
            spacing={24}
            style={{
              margin: '1rem 0 0.5rem',
              justifyContent: 'flex-start',
              display: 'flex'
            }}
          >
            <Grid item style={{ paddingLeft: 0 }}>
              <StyledButton
                disabled={loading || isMissingRequiredFields() || hasInvalidInputs() || isInvalidPostalCode()}
                color="secondary"
                variant="contained"
                onClick={() => handleSave(addressDto)}
              >
                Save
              </StyledButton>
              {loading && (
                <CircularProgress
                  size={20}
                  style={{
                    marginLeft: '20px',
                    marginTop: '12px'
                  }}
                />
              )}
            </Grid>
            <Grid item>
              <StyledButton disabled={loading} color="secondary" variant="contained" onClick={() => handleClose()}>
                Cancel
              </StyledButton>
            </Grid>
          </Grid>
        </Grid>
      </StyledDialogContent>
    </Fragment>

  )
}

export default EditLocationAddressModal
